import apiClient from "@/api/client";
import type {
  B2BInvoice,
  B2BInvoiceComment,
  B2BInvoicePeriod,
  B2BInvoiceFormData,
  LaborCostCheckParams,
  LaborCostWarning,
  MissingForMonth,
} from "../types";

const BASE = "/b2b-invoices";

function toFormData(payload: Partial<B2BInvoiceFormData>): FormData {
  const fd = new FormData();
  for (const [k, v] of Object.entries(payload)) {
    if (v === undefined || v === null) continue;
    if (v instanceof File) {
      fd.append(k, v, v.name);
    } else {
      fd.append(k, String(v));
    }
  }
  return fd;
}

export const b2bInvoicesApi = {
  list: (params?: Record<string, string | number | undefined>) =>
    apiClient
      .get<{ data: B2BInvoice[]; meta: { count: number } }>(`${BASE}/`, { params })
      .then((r) => r.data),

  get: (id: string) =>
    apiClient
      .get<{ data: B2BInvoice }>(`${BASE}/${id}/`)
      .then((r) => r.data.data),

  create: (data: B2BInvoiceFormData) => {
    const hasFile = data.invoice_file instanceof File;
    if (hasFile) {
      return apiClient
        .post<{ data: B2BInvoice }>(`${BASE}/`, toFormData(data), {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((r) => r.data.data);
    }
    const { invoice_file, ...rest } = data;
    return apiClient
      .post<{ data: B2BInvoice }>(`${BASE}/`, rest)
      .then((r) => r.data.data);
  },

  update: (id: string, data: Partial<B2BInvoiceFormData>) => {
    const hasFile = data.invoice_file instanceof File;
    if (hasFile) {
      return apiClient
        .patch<{ data: B2BInvoice }>(`${BASE}/${id}/`, toFormData(data), {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((r) => r.data.data);
    }
    const { invoice_file, ...rest } = data;
    return apiClient
      .patch<{ data: B2BInvoice }>(`${BASE}/${id}/`, rest)
      .then((r) => r.data.data);
  },

  remove: (id: string) => apiClient.delete(`${BASE}/${id}/`),

  submit: (id: string) =>
    apiClient
      .post<{ data: B2BInvoice }>(`${BASE}/${id}/submit/`)
      .then((r) => r.data.data),

  approve: (id: string) =>
    apiClient
      .post<{ data: B2BInvoice }>(`${BASE}/${id}/approve/`)
      .then((r) => r.data.data),

  reject: (id: string, rejection_reason: string) =>
    apiClient
      .post<{ data: B2BInvoice }>(`${BASE}/${id}/reject/`, { rejection_reason })
      .then((r) => r.data.data),

  markBooked: (id: string) =>
    apiClient
      .post<{ data: B2BInvoice }>(`${BASE}/${id}/mark-booked/`)
      .then((r) => r.data.data),

  markTransferred: (id: string) =>
    apiClient
      .post<{ data: B2BInvoice }>(`${BASE}/${id}/mark-transferred/`)
      .then((r) => r.data.data),

  markPaid: (id: string) =>
    apiClient
      .post<{ data: B2BInvoice }>(`${BASE}/${id}/mark-paid/`)
      .then((r) => r.data.data),

  laborCostCheck: (params: LaborCostCheckParams) =>
    apiClient
      .get<{ data: LaborCostWarning }>(`${BASE}/labor-cost-check/`, { params })
      .then((r) => r.data.data),

  missingForMonth: () =>
    apiClient
      .get<{ data: MissingForMonth }>(`${BASE}/missing-for-month/`)
      .then((r) => r.data.data),

  periods: () =>
    apiClient
      .get<{ data: B2BInvoicePeriod[] }>(`${BASE}/periods/`)
      .then((r) => r.data.data),

  actionCount: () =>
    apiClient
      .get<{ data: { count: number } }>(`${BASE}/action-count/`)
      .then((r) => r.data.data.count),

  // ── Dyskusja pod fakturą (autor faktury / CEO / księgowość) ─────────────────
  listComments: (invoiceId: string) =>
    apiClient
      .get<{ data: B2BInvoiceComment[] }>(`${BASE}/${invoiceId}/comments/`)
      .then((r) => r.data.data),

  addComment: (invoiceId: string, content: string, mentionIds: string[]) =>
    apiClient
      .post<{ data: B2BInvoiceComment }>(`${BASE}/${invoiceId}/comments/`, {
        content,
        mention_ids: mentionIds,
      })
      .then((r) => r.data.data),

  deleteComment: (invoiceId: string, commentId: string) =>
    apiClient.delete(`${BASE}/${invoiceId}/comments/${commentId}/`),
};
